import * as React from 'react'

import { clsx } from 'clsx'

import { VisuallyHidden } from '@buffer-mono/popcorn'

import { getReactNodeEntries } from '~publish/helpers/getReactNodeEntries'

import styles from './MediaCombinedPreview.module.css'

import type { JSX } from 'react'

type MediaCombinedPreviewProps = {
  children?: React.ReactNode
  className?: string
}

/**
 * Displays images in a bento-box like layout with maximum of 4 images.
 * If there are more than 4 images, a marker will be displayed indicating the number of additional images.
 */
const MediaCombinedPreview = ({
  children,
  className,
  ...props
}: MediaCombinedPreviewProps): JSX.Element | null => {
  const mediaEntries = getReactNodeEntries(children)
  const displayedMediaEntries = mediaEntries.slice(0, 4)
  const moreCount = mediaEntries.length - displayedMediaEntries.length

  return (
    <div
      className={clsx(styles.container, className)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The preview is a labelled group of related media items.
      role="group"
      aria-label="Media preview"
      data-media-count={displayedMediaEntries.length}
      {...props}
    >
      {displayedMediaEntries.map(({ node, key }, index) => {
        const isLast = index === displayedMediaEntries.length - 1
        return (
          <div
            key={key}
            className={styles.item}
            data-more-count={isLast && moreCount > 0 ? moreCount : undefined}
          >
            {node}
            {isLast && moreCount > 0 && (
              <VisuallyHidden>{moreCount} more media</VisuallyHidden>
            )}
          </div>
        )
      })}
    </div>
  )
}

MediaCombinedPreview.displayName = 'MediaCombinedPreview'

export { MediaCombinedPreview }
